// The React-free half of data/hooks.ts: decoded lists and the derived views over them, each built once per distinct
// input for every component (sharedMemo). Rows are immutable (a changed row is a new object) and the replica keeps a
// collection's row array until that collection changes, so:
// - a row decodes once per version however many screens read it (decodeRow's cache);
// - every component reading a collection gets the same DataList until the collection changes;
// - visible users / stores, derived stats, the metrics index, lookups and positions are computed once per change.

import { SYNC_COLLECTIONS, type Collection } from '@/contracts/collections';
import {
  MODELS,
  decodeSettings,
  type ActivityPayload,
  type DayLogPayload,
  type PayloadOf,
  type PipelinePayload,
  type RecordPayload,
  type SettingsPayload,
  type TeamPayload,
  type TerritoryPayload,
  type UserPayload,
  type VisitResultPayload,
} from '@/contracts/models';
import { idKey } from '@/contracts/uuid';
import type { Role } from '@/contracts/wire';
import { recomputeDerivedStats, type DerivedStats } from '@/domain/derived';
import { buildMetricsIndex, type MetricsIndex } from '@/domain/metrics';
import { recordLookups, type RecordLookups } from '@/domain/records';
import { lastPositions, type RepPosition } from '@/domain/team';
import { visibleRecords, visibleUsers } from '@/domain/visibility';
import { decodeRow } from '@/sync/hooks';
import type { CachedRow } from '@/sync/replica';
import { sharedMemo } from './shared';

/** One decoded row with its sync meta. Treat as read-only. */
export interface DataItem<T> {
  /** Upper-case id (the envelope id; equals `value.id` except for settings). */
  id: string;
  /** The decoded payload (best effort when `undecodable`). Unknown keys from newer apps are kept. */
  value: T;
  /** Envelope `modified_at`: "Last updated" (never the payload's stale `updatedAt`). */
  modifiedAt: string;
  /** Device id of the last writer (`web-<uuid>` for the console). */
  modifiedBy: string;
  /** Server version (0 for a new item not saved yet). */
  version: number;
  serverSeq: number;
  /** attachments/documents: `<company>/<collection>/<fileName>` (for `useFileUrl`). */
  filePath?: string;
  /** Set when an iPhone could not decode this item (created by a newer app): show read-only, never edit. */
  undecodable?: string;
}

/** A decoded collection (or a filtered view of one). */
export interface DataList<T> {
  /** Every live row in replica order, undecodable ones included. */
  readonly items: readonly DataItem<T>[];
  /** Decoded values of the decodable items, same order: pass these to domain/ functions. */
  readonly values: readonly T[];
  /** Items by lower-case id. */
  readonly byId: ReadonlyMap<string, DataItem<T>>;
  /** Item by id (any case). */
  get(id: string | null | undefined): DataItem<T> | undefined;
  /** Row `modified_at` by id (pipelineColumns' `modifiedAt` lookup). */
  modifiedAt(id: string): string | undefined;
}

type Decoder<T> = (row: CachedRow) => DataItem<T>;

function makeDecoder<C extends Collection>(collection: C): Decoder<PayloadOf<C>> {
  const spec = MODELS[collection];
  return (row) => {
    const decoded = spec.decode(row.payload, spec.hasId ? row.id : undefined);
    const item: DataItem<PayloadOf<C>> = {
      id: row.id,
      value: decoded.value,
      modifiedAt: row.modifiedAt,
      modifiedBy: row.modifiedBy,
      version: row.version,
      serverSeq: row.serverSeq,
    };
    if (row.filePath !== undefined) item.filePath = row.filePath;
    if (decoded.undecodable !== undefined) item.undecodable = decoded.undecodable;
    return item;
  };
}

// Module-level decoders: decodeRow memoises per (decoder, row object), so these must be stable functions.
const DECODERS = Object.fromEntries(SYNC_COLLECTIONS.map((c) => [c, makeDecoder(c)])) as {
  readonly [C in Collection]: Decoder<PayloadOf<C>>;
};

/** One row decoded (memoised per row object); null when the decoder threw. */
export function decodeItem<C extends Collection>(collection: C, row: CachedRow): DataItem<PayloadOf<C>> | null {
  return decodeRow(row, DECODERS[collection]);
}

function makeList<T>(items: readonly DataItem<T>[]): DataList<T> {
  const byId = new Map<string, DataItem<T>>();
  const values: T[] = [];
  for (const item of items) {
    byId.set(idKey(item.id), item);
    if (item.undecodable === undefined) values.push(item.value);
  }
  return {
    items,
    values,
    byId,
    get: (id) => (id ? byId.get(idKey(id)) : undefined),
    modifiedAt: (id) => byId.get(idKey(id))?.modifiedAt,
  };
}

/** A sub-list of `list` holding the items whose value is in `keep` (by id), in `list` order. */
function subList<T extends { id: string }>(list: DataList<T>, keep: readonly T[]): DataList<T> {
  const ids = new Set(keep.map((v) => idKey(v.id)));
  return makeList(list.items.filter((item) => ids.has(idKey(item.id))));
}

const EMPTY_LIST = makeList<never>([]);

const decodeRowsShared = sharedMemo((collection: Collection, rows: readonly CachedRow[]): DataList<unknown> => {
  const items: DataItem<unknown>[] = [];
  for (const row of rows) {
    const item = decodeItem(collection, row);
    if (item) items.push(item);
  }
  return makeList(items);
});

/** A collection's live rows, decoded: the same DataList for every caller until that row array changes. */
export function decodeRows<C extends Collection>(collection: C, rows: readonly CachedRow[]): DataList<PayloadOf<C>> {
  if (rows.length === 0) return EMPTY_LIST as DataList<PayloadOf<C>>;
  return decodeRowsShared(collection, rows) as DataList<PayloadOf<C>>;
}

/** `CompanySettings()`: what every screen shows before the company's settings item has arrived. */
export const DEFAULT_SETTINGS: Readonly<SettingsPayload> = Object.freeze(decodeSettings({}).value);

/** `visibleUsers` as a sub-list (everyone without a role, i.e. outside a company session). */
export const selectVisibleUsers = sharedMemo(
  (users: DataList<UserPayload>, teams: DataList<TeamPayload>, profileId: string | null, role: Role | null): DataList<UserPayload> => {
    if (!role) return users;
    return subList(users, visibleUsers(users.items.map((i) => i.value), teams.values, { profileId, role }));
  },
);

/** `visibleRecords` as a sub-list (without a role: every store, archived ones only with `includeArchived`). */
export const selectVisibleRecords = sharedMemo(
  (
    records: DataList<RecordPayload>,
    users: DataList<UserPayload>,
    teams: DataList<TeamPayload>,
    territories: DataList<TerritoryPayload>,
    settings: SettingsPayload,
    profileId: string | null,
    role: Role | null,
    includeArchived: boolean,
  ): DataList<RecordPayload> => {
    const all = records.items.map((i) => i.value);
    const visible = role
      ? visibleRecords({
          records: all,
          users: users.values,
          teams: teams.values,
          territories: territories.values,
          settings,
          viewer: { profileId, role },
          includeArchived,
        })
      : includeArchived
        ? all
        : all.filter((r) => !r.isArchived);
    return subList(records, visible);
  },
);

/** Derived record stats from the decodable activities (what an iPhone computes). */
export const selectDerivedStats = sharedMemo(
  (activities: DataList<ActivityPayload>): DerivedStats => recomputeDerivedStats(activities.values),
);

/** The metrics index over every decodable activity and record. */
export const selectMetricsIndex = sharedMemo(
  (activities: DataList<ActivityPayload>, records: DataList<RecordPayload>, visitResults: DataList<VisitResultPayload>): MetricsIndex =>
    buildMetricsIndex({ activities: activities.values, records: records.values, visitResults: visitResults.values }),
);

/** Pin / legend / filter lookups; `day` (local midnight, epoch ms) rebuilds them when the date changes. */
export const selectRecordLookups = sharedMemo(
  (
    stats: DerivedStats,
    visitResults: DataList<VisitResultPayload>,
    users: DataList<UserPayload>,
    pipelines: DataList<PipelinePayload>,
    day: number,
  ): RecordLookups => {
    void day; // part of the cache key only
    return recordLookups({ stats, visitResults: visitResults.values, users: users.values, pipelines: pipelines.values, now: new Date() });
  },
);

/** Last known rep positions from the decodable day logs. */
export const selectRepPositions = sharedMemo(
  (dayLogs: DataList<DayLogPayload>): ReadonlyMap<string, RepPosition> => lastPositions(dayLogs.values),
);
